export interface ActividadAdicionalFormPayload {
  actividadId: string;
  descripcion: string;
  fechaVencimientoProfase: string;
  requiereArchivo: boolean;
}

/** @deprecated Use ActividadAdicionalFormPayload */
export type CrearActividadAdicionalPayload = ActividadAdicionalFormPayload;

const WF_ESTADOS_BLOQUEADOS = new Set([
  'FINALIZADO',
  'FINALIZADO_Y_ARCHIVADO',
  'ANULADO',
]);

const ACTIVIDAD_ESTADOS_NO_EDITABLES = new Set([
  'FINALIZADO',
  'BLOQUEADO',
]);

function authHeader(): Record<string, string> {
  const token = localStorage.getItem('jhi-authenticationToken');
  return token ? { Authorization: `Bearer ${token}` } : {};
}

function asRecord(raw: unknown): Record<string, unknown> | null {
  if (!raw || typeof raw !== 'object') return null;
  return raw as Record<string, unknown>;
}

export function isActividadAdicional(raw: unknown): boolean {
  const r = asRecord(raw);
  if (!r) return false;
  const nodo = r.nodoInstancia as Record<string, unknown> | undefined;
  return r.actividadAdicional === true || nodo?.adicional === true;
}

export function resolveWorkflowInstanciaId(raw: unknown): number | null {
  const r = asRecord(raw);
  if (!r) return null;
  const data = r.data as Record<string, unknown> | undefined;
  const nodo = r.nodoInstancia as Record<string, unknown> | undefined;
  const wf = r.workflowInstancia as { id?: unknown } | undefined;

  const candidates = [
    r.workflowInstanciaId,
    wf?.id,
    data?.workflowInstanciaId,
    (data?.workflowInstancia as { id?: unknown } | undefined)?.id,
    nodo?.workflowInstanciaId,
    (nodo?.workflowInstancia as { id?: unknown } | undefined)?.id,
  ];

  for (const c of candidates) {
    const n = Number(c);
    if (Number.isFinite(n) && n > 0) return n;
  }
  return null;
}

export function resolveWorkflowInstanciaEstado(raw: unknown): string {
  const r = asRecord(raw);
  if (!r) return '';
  const data = r.data as Record<string, unknown> | undefined;
  const wf = r.workflowInstancia as { estado?: unknown } | undefined;
  const nodoWf = (r.nodoInstancia as { workflowInstancia?: { estado?: unknown } } | undefined)
    ?.workflowInstancia;

  return String(
    wf?.estado
    ?? nodoWf?.estado
    ?? r.workflowInstanciaEstado
    ?? data?.workflowInstanciaEstado
    ?? '',
  ).toUpperCase();
}

export function resolveActividadEstadoFromRaw(raw: unknown): string {
  const r = asRecord(raw);
  if (!r) return '';
  return String(r.estado ?? '').toUpperCase();
}

export function resolveCategoriaActividadId(raw: unknown): number | null {
  const r = asRecord(raw);
  if (!r) return null;
  const data = r.data as Record<string, unknown> | undefined;
  const nodo = r.nodoInstancia as Record<string, unknown> | undefined;
  const nodoAdicional = nodo?.workflowNodoAdicional as Record<string, unknown> | undefined;
  const actividad = nodoAdicional?.actividad as { id?: unknown } | undefined;

  const candidates = [
    r.categoriaActividadId,
    data?.categoriaActividadId,
    actividad?.id,
    nodoAdicional?.categoriaActividadId,
  ];

  for (const c of candidates) {
    const n = Number(c);
    if (Number.isFinite(n) && n > 0) return n;
  }
  return null;
}

export function resolveCategoriaActividadLabel(raw: unknown): string {
  const r = asRecord(raw);
  if (!r) return '';
  const data = r.data as Record<string, unknown> | undefined;
  const nodo = r.nodoInstancia as Record<string, unknown> | undefined;
  const nodoAdicional = nodo?.workflowNodoAdicional as Record<string, unknown> | undefined;
  const actividad = nodoAdicional?.actividad as { descripcion?: unknown } | undefined;

  const value = r.categoriaActividadDescripcion
    ?? data?.label
    ?? actividad?.descripcion
    ?? r.asunto;
  return value != null ? String(value).trim() : '';
}

export function resolveDescripcionAdicional(raw: unknown): string {
  const r = asRecord(raw);
  if (!r) return '';
  const data = r.data as Record<string, unknown> | undefined;
  const nodo = r.nodoInstancia as Record<string, unknown> | undefined;
  const nodoAdicional = nodo?.workflowNodoAdicional as { descripcion?: unknown } | undefined;
  const value = r.descripcion
    ?? r.mensaje
    ?? data?.descripcion
    ?? nodoAdicional?.descripcion;
  return value != null ? String(value).trim() : '';
}

export function resolveFechaVencimientoProfase(raw: unknown): string | null {
  const r = asRecord(raw);
  if (!r) return null;
  const data = r.data as Record<string, unknown> | undefined;
  const nodo = r.nodoInstancia as { fechaVencimientoProfase?: unknown } | undefined;
  const value = r.fechaVencimientoProfase ?? data?.fechaVencimientoProfase ?? nodo?.fechaVencimientoProfase;
  if (value == null || value === '') return null;
  return String(value);
}

export function resolveRequiereArchivoAdicional(raw: unknown): boolean {
  const r = asRecord(raw);
  if (!r) return false;
  const data = r.data as Record<string, unknown> | undefined;
  const nodo = r.nodoInstancia as { requiereArchivo?: unknown } | undefined;
  const value = r.requiereArchivo ?? data?.requiereArchivo ?? nodo?.requiereArchivo;
  return value === true || value === 'true' || value === 'S' || value === 1 || value === '1';
}

export function toDateInputValue(value: unknown): string {
  if (value == null || value === '') return '';
  const s = String(value).trim();
  if (/^\d{4}-\d{2}-\d{2}/.test(s)) return s.slice(0, 10);
  const d = new Date(s);
  if (Number.isNaN(d.getTime())) return '';
  return d.toISOString().slice(0, 10);
}

export function actividadAdicionalFormFromTask(raw: unknown): ActividadAdicionalFormPayload {
  const actividadId = resolveCategoriaActividadId(raw);
  return {
    actividadId: actividadId != null ? String(actividadId) : '',
    descripcion: resolveDescripcionAdicional(raw),
    fechaVencimientoProfase: toDateInputValue(resolveFechaVencimientoProfase(raw)),
    requiereArchivo: resolveRequiereArchivoAdicional(raw),
  };
}

export function puedeCrearActividadAdicional(raw: unknown): boolean {
  if (!resolveWorkflowInstanciaId(raw)) return false;
  const estado = resolveWorkflowInstanciaEstado(raw);
  if (estado && WF_ESTADOS_BLOQUEADOS.has(estado)) return false;
  return true;
}

function actividadAdicionalBloqueadaPorEstado(raw: unknown): boolean {
  if (!isActividadAdicional(raw)) return true;
  const actividadEstado = resolveActividadEstadoFromRaw(raw);
  if (ACTIVIDAD_ESTADOS_NO_EDITABLES.has(actividadEstado)) return true;
  const wfEstado = resolveWorkflowInstanciaEstado(raw);
  if (wfEstado && WF_ESTADOS_BLOQUEADOS.has(wfEstado)) return true;
  return false;
}

export function puedeEditarActividadAdicional(raw: unknown): boolean {
  return isActividadAdicional(raw) && !actividadAdicionalBloqueadaPorEstado(raw);
}

export function puedeEliminarActividadAdicional(raw: unknown): boolean {
  return puedeEditarActividadAdicional(raw);
}

export function actividadAdicionalEditDisabledReason(raw: unknown): string | null {
  if (!isActividadAdicional(raw)) return 'Solo aplica a actividades adicionales.';
  const actividadEstado = resolveActividadEstadoFromRaw(raw);
  if (ACTIVIDAD_ESTADOS_NO_EDITABLES.has(actividadEstado)) {
    return `No se puede modificar en estado ${actividadEstado}.`;
  }
  const wfEstado = resolveWorkflowInstanciaEstado(raw);
  if (wfEstado && WF_ESTADOS_BLOQUEADOS.has(wfEstado)) {
    return `El proceso está en estado ${wfEstado}.`;
  }
  return null;
}

export function validateActividadAdicionalForm(
  values: ActividadAdicionalFormPayload,
): string | null {
  const actividadId = values.actividadId.trim();
  if (!actividadId) return 'La actividad es obligatoria.';
  const id = Number(actividadId);
  if (!Number.isFinite(id) || id <= 0) return 'Seleccione una actividad válida del catálogo.';
  return null;
}

async function parseApiErrorMessage(res: Response, fallback: string): Promise<string> {
  let msg = fallback;
  try {
    const text = await res.text();
    if (text) {
      try {
        const body = JSON.parse(text);
        msg = body?.details ?? body?.detail ?? body?.message ?? body?.title ?? body?.error ?? text;
      } catch {
        msg = text;
      }
    }
  } catch { /* ignore */ }
  return msg;
}

function buildActividadAdicionalBody(payload: ActividadAdicionalFormPayload): Record<string, unknown> {
  const body: Record<string, unknown> = {
    actividadId: Number(payload.actividadId),
    descripcion: payload.descripcion.trim(),
    requiereArchivo: payload.requiereArchivo,
  };
  const fechaProfase = payload.fechaVencimientoProfase.trim();
  if (fechaProfase) body.fechaVencimientoProfase = fechaProfase;
  return body;
}

export async function crearActividadAdicional(
  workflowInstanciaId: number,
  payload: ActividadAdicionalFormPayload,
): Promise<unknown> {
  const res = await fetch(
    `/api/profase-workflow-instancias/${workflowInstanciaId}/actividades-adicionales`,
    {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', ...authHeader() },
      body: JSON.stringify(buildActividadAdicionalBody(payload)),
    },
  );

  if (!res.ok) {
    throw new Error(await parseApiErrorMessage(res, 'No se pudo crear la actividad adicional.'));
  }

  return res.json();
}

export async function actualizarActividadAdicional(
  actividadId: string | number,
  payload: ActividadAdicionalFormPayload,
): Promise<unknown> {
  const res = await fetch(`/api/usuario/me/actividad/${actividadId}/adicional`, {
    method: 'PUT',
    headers: { 'Content-Type': 'application/json', ...authHeader() },
    body: JSON.stringify(buildActividadAdicionalBody(payload)),
  });

  if (!res.ok) {
    throw new Error(await parseApiErrorMessage(res, 'No se pudo actualizar la actividad adicional.'));
  }

  return res.json();
}

export async function eliminarActividadAdicional(actividadId: string | number): Promise<void> {
  const res = await fetch(`/api/usuario/me/actividad/${actividadId}`, {
    method: 'DELETE',
    headers: { ...authHeader() },
  });

  if (!res.ok) {
    throw new Error(await parseApiErrorMessage(res, 'No se pudo eliminar la actividad adicional.'));
  }
}
